import React, { useEffect, useRef, useState, useId } from 'react';
import { useAuth } from '../context/AuthContext';

const GoogleAuth = ({ customClass, onGoogleLogin }) => {
  const { handleGoogleLogin, googleLoginLoading, startGoogleLoading, stopGoogleLoading } = useAuth();
  const initialized = useRef(false);
  const [isCustomButton] = useState(!!customClass);
  const uniqueId = useId();
  const buttonId = `google-signin-button-${uniqueId.replace(/:/g, '-')}`;

  useEffect(() => {
    initialized.current = false;
    
    const loadGoogleScript = () => {
      if (document.querySelector(`script[src="https://accounts.google.com/gsi/client"]`)) {
        setTimeout(initGoogleButton, 100);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      script.onload = () => {
        initGoogleButton();
      };
    };

    const initGoogleButton = () => {
      if (!window.google) {
        return;
      }
      
      const buttonElement = document.getElementById(buttonId);
      if (!buttonElement) {
        return;
      }
      
      if (initialized.current) {
        return;
      }
      
      try {
        window.google.accounts.id.initialize({
          client_id: '391839016204-m3lknjhp2p5l2v2r7c38vb605ja64ddb.apps.googleusercontent.com',
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        
        window.google.accounts.id.disableAutoSelect();

        window.google.accounts.id.renderButton(
          buttonElement,
          { 
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: 240
          }
        );
        
        initialized.current = true;
      } catch (error) {
        // Silently handle initialization errors
      }
    };

    const handleCredentialResponse = async (response) => {
      console.log('🟢 [GOOGLE] Google credential response received, starting loading...');
      startGoogleLoading();
      
      if (!response.credential) {
        stopGoogleLoading();
        return;
      }
      
      try {
        const payload = parseJwt(response.credential);
        
        const userData = {
          id: payload.sub,
          name: payload.name,
          email: payload.email,
          picture: payload.picture,
          firstName: payload.given_name,
          lastName: payload.family_name,
        };
        
        const tokens = {
          id_token: response.credential,
          expires_at: Date.now() + (3600 * 1000),
          token_type: 'Bearer'
        };
        
        console.log('🟢 [GOOGLE] Google login successful, calling handleGoogleLogin...');
        await handleGoogleLogin(userData, tokens);
        
        if (onGoogleLogin) {
          console.log('🟢 [GOOGLE] Calling onGoogleLogin callback...');
          onGoogleLogin(userData);
        }
      } catch (error) {
        console.error('🔴 [GOOGLE] Google login failed:', error);
        stopGoogleLoading();
      }
    };

    const parseJwt = (token) => {
      try {
        return JSON.parse(atob(token.split('.')[1]));
      } catch (e) {
        return null;
      }
    };

    loadGoogleScript();
    
    return () => {};
  }, [handleGoogleLogin, startGoogleLoading, stopGoogleLoading, onGoogleLogin, buttonId]);

  const handleCustomButtonClick = () => {
    if (window.google && window.google.accounts) {
      window.google.accounts.id.prompt();
    }
  };

  if (isCustomButton) {
    return (
      <div>
        <button 
          className={customClass || ""}
          onClick={handleCustomButtonClick}
          disabled={googleLoginLoading}
        >
          {googleLoginLoading ? (
            <>
              <svg className="animate-spin w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Connexion...
            </>
          ) : (
            <>
              <img src="https://cdn.cdnlogo.com/logos/g/35/google-icon.svg" className="w-5 h-5 mr-2" alt="Google" />
              Google
            </>
          )}
        </button>
        <div id={buttonId} style={{ display: 'none' }}></div>
      </div>
    );
  }
  
  return (
    <div className="relative flex justify-center">
      <div id={buttonId} style={{ opacity: googleLoginLoading ? 0 : 1 }}></div>
      {googleLoginLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-700 rounded">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-cyan-500 border-t-transparent"></div>
        </div>
      )}
    </div>
  );
};

export default GoogleAuth;
